import type { Recipe, ListRecipesParams } from "../types";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3001";

async function fetchJson<T>(
  path: string,
  params?: Record<string, string>
): Promise<T> {
  const url = new URL(`${API_BASE}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "") url.searchParams.set(k, v);
    });
  }
  const res = await fetch(url.toString());
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function mutateJson<T>(
  method: string,
  path: string,
  body: unknown,
  token: string
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function fetchRecipes(params?: ListRecipesParams): Promise<Recipe[]> {
  const queryParams: Record<string, string> = {};
  if (params?.course) queryParams.course = params.course;
  if (params?.cuisine) queryParams.cuisine = params.cuisine;
  if (params?.tag) queryParams.tag = params.tag;
  if (params?.favorite) queryParams.favorite = "true";
  return fetchJson<Recipe[]>("/recipes", queryParams);
}

export async function fetchRecipe(slug: string): Promise<Recipe> {
  return fetchJson<Recipe>(`/recipes/${encodeURIComponent(slug)}`);
}

export async function createRecipe(
  data: Partial<Recipe>,
  token: string
): Promise<Recipe> {
  return mutateJson<Recipe>("POST", "/recipes", data, token);
}

export async function updateRecipe(
  recipeId: string,
  data: Partial<Recipe>,
  token: string
): Promise<Recipe> {
  return mutateJson<Recipe>("PUT", `/recipes/${recipeId}`, data, token);
}

export async function deleteRecipe(
  recipeId: string,
  token: string
): Promise<void> {
  return mutateJson<void>("DELETE", `/recipes/${recipeId}`, undefined, token);
}

export async function getUploadUrl(
  filename: string,
  contentType: string,
  token: string
): Promise<{ uploadUrl: string; imageKey: string }> {
  return mutateJson<{ uploadUrl: string; imageKey: string }>(
    "POST",
    "/recipes/upload-url",
    { filename, contentType },
    token
  );
}

export async function importRecipeFromUrl(
  url: string,
  token: string
): Promise<Record<string, unknown>> {
  return mutateJson<Record<string, unknown>>(
    "POST",
    "/recipes/import-url",
    { url },
    token
  );
}
